import { describeApiError, type FantasyContest, type Leaderboard } from '@brush/api-client';
import { ErrorNotice, ErrorRetry, Pill, Spinner } from '@brush/ui';
import { Plus, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  CONTEST_STATUSES,
  CONTEST_STATUS_LABEL,
  CONTEST_STATUS_TONE,
  contestStatusOf,
  formatInstant,
  pausedNotice,
  rosterFormatLabel,
  withUnit,
  budgetUnit,
} from '../../fantasy/contest';
import { useCurrentUser, useFantasyContests, useLeaderboards } from '../../queries';
import { useRoom } from '../../room';
import { useMainScroll } from '../../scroll';
import { useUnsaved } from '../../unsaved';
import { ContestBuilderScreen } from './ContestBuilderScreen';
import { NewContestDialog } from './NewContestDialog';

/**
 * §9.1's contest list: what this room is running, at what stage, with how many entries.
 *
 * <b>Ordered by §6's lifecycle rather than by date</b>, and that is the one arrangement decision
 * worth stating. A contest list is not an archive — the thing an operator came here to do is
 * almost always to the contest that is *waiting on them*, and that ordering puts unpublished
 * drafts at the top and finished contests at the bottom, which is the order of how much attention
 * each wants. Within a stage it is alphabetical — see `byLifecycle` for why not by date.
 *
 * A card rather than a table row, matching Leaderboards next door: a contest carries five facts
 * that are read together (what it scores from, what it costs to enter a roster, how big a roster
 * is, when it locks, how many people have entered) and a table of those is five columns nobody
 * scans.
 */
export function ContestsView() {
  const { room, roomId } = useRoom();
  const { leave } = useUnsaved();
  const scrollToTop = useMainScroll();

  // §6.2's drift report and its Adopt are administrator-only, so the builder needs to know which
  // caller it is rendering for. Read here rather than inside the panel, following the leaderboards
  // view: the entitlement is a fact about the session, not about the contest.
  const me = useCurrentUser();

  const contestsQ = useFantasyContests(roomId);
  const leaderboardsQ = useLeaderboards(roomId);

  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Opening or closing the builder swaps a whole screen and the two are wildly different heights.
  // Without this the operator lands part-way down a shorter one, looking at blank space.
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, openId]);

  if (contestsQ.isLoading) return <Spinner label="Loading contests…" />;
  if (contestsQ.isError || !room) {
    return (
      <div className="p-8">
        <ErrorRetry
          message="Couldn't load this room's contests."
          onRetry={() => void contestsQ.refetch()}
        />
      </div>
    );
  }

  const contests = contestsQ.data ?? [];
  const leaderboards = leaderboardsQ.data ?? [];

  // Looked up by id every render rather than held, so the screen sees a transition the moment the
  // list refetches. Leaving routes through the unsaved-work guard, exactly as the section nav and
  // the room switcher do.
  const open = openId ? (contests.find((c) => c.id === openId) ?? null) : null;

  if (open) {
    return (
      <div className="p-8">
        <ContestBuilderScreen
          contest={open}
          isAdmin={me.data?.isAdmin ?? false}
          onBack={() => leave(() => setOpenId(null))}
          onDeleted={() => setOpenId(null)}
        />
      </div>
    );
  }

  // A contest scores from a leaderboard and inherits its events (locked rule 1), so a room with
  // none has nothing to build one on. Said before the button is reached for rather than after.
  const noLeaderboards = !leaderboardsQ.isLoading && leaderboards.length === 0;

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink-950">Fantasy contests</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-800/60">
            Entrants build a team from your players under a budget, and score whatever those
            players score on the leaderboard behind it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={noLeaderboards}
          title={noLeaderboards ? 'Create a leaderboard first' : undefined}
          className="inline-flex items-center gap-2 bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="size-4" /> New contest
        </button>
      </div>

      {noLeaderboards ? (
        <p className="mb-5 border border-line border-l-4 border-l-warn bg-surface p-4 text-sm leading-relaxed text-ink-900">
          A contest scores from a leaderboard and takes its event list from one, so there is
          nothing to build on yet. Create a leaderboard first — then a contest turns it into
          something people can play along with.
        </p>
      ) : null}

      <ErrorNotice message={describeApiError(leaderboardsQ.error)} className="mb-4" />

      {contests.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {byLifecycle(contests).map((contest) => (
            <ContestCard
              key={contest.id}
              contest={contest}
              leaderboard={leaderboards.find((l) => l.id === contest.leaderboardId) ?? null}
              leaderboardsKnown={leaderboardsQ.isSuccess}
              onOpen={() => setOpenId(contest.id)}
            />
          ))}
        </div>
      )}

      {creating ? (
        <NewContestDialog
          leaderboards={leaderboards}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            setOpenId(created.id);
          }}
        />
      ) : null}
    </div>
  );
}

function ContestCard({
  contest,
  leaderboard,
  leaderboardsKnown,
  onOpen,
}: {
  contest: FantasyContest;
  leaderboard: Leaderboard | null;
  /** Whether the leaderboard list arrived. Distinguishes "gone" from "not asked yet". */
  leaderboardsKnown: boolean;
  onOpen: () => void;
}) {
  const status = contestStatusOf(contest);
  const unit = budgetUnit(contest);

  return (
    <article className="border border-line bg-surface p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate font-display font-semibold text-ink-950">{contest.name}</h2>
          <p className="mt-0.5 text-xs text-ink-800/60">
            {rosterFormatLabel(contest.rosterFormat)} · {contest.rosterSize} players ·{' '}
            {withUnit(contest.budget, unit)}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {/* Beside the status rather than replacing it: the contest is still locked and still
              taking no entries, and what has changed is that it has stopped scoring. */}
          {contest.standingsPaused ? <Pill tone="warn">Paused</Pill> : null}
          <Pill tone={CONTEST_STATUS_TONE[status]}>{CONTEST_STATUS_LABEL[status]}</Pill>
        </div>
      </div>

      {contest.standingsPaused ? (
        <p className="mb-3 border border-line border-l-4 border-l-warn bg-surface p-2.5 text-[11px] leading-relaxed text-ink-900">
          {pausedNotice(contest.standingsPausedAt)}
        </p>
      ) : null}

      <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2 border-y border-line py-3 text-xs">
        <Fact label="Scores from">
          {leaderboard ? (
            <span className="text-ink-950">{leaderboard.name}</span>
          ) : leaderboardsKnown ? (
            // Not an error state on this card: the contest is intact and still scores whatever the
            // server resolves. What it means is that this operator cannot see that board.
            <span className="text-ink-800/50">Not in this room’s list</span>
          ) : (
            <span className="text-ink-800/40">—</span>
          )}
        </Fact>
        <Fact label="Entries">
          {/* Zero is a real answer for a draft and for an open contest nobody has found yet, so it
              is printed rather than dashed. */}
          <span className="num text-ink-950">{contest.entries}</span>
          {contest.maxEntriesPerPlayer > 1 ? (
            <span className="text-ink-800/50"> · up to {contest.maxEntriesPerPlayer} each</span>
          ) : null}
        </Fact>
        <Fact label={status === 'draft' ? 'Locks' : status === 'open' ? 'Locks' : 'Locked'}>
          <span className="num text-ink-950">
            {status === 'draft' && !contest.locksAt
              ? 'When the first event starts'
              : formatInstant(status === 'draft' || status === 'open' ? contest.locksAt : contest.lockedAt)}
          </span>
        </Fact>
        <Fact label="Captain">
          <span className="text-ink-950">
            {contest.captainEnabled
              ? `×${contest.captainMultiplier ?? 1} on one player`
              : 'Not used'}
          </span>
        </Fact>
      </dl>

      <button
        type="button"
        onClick={onOpen}
        className="w-full border border-line bg-surface py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950"
      >
        {/* The verb is the state's, not a flat "Open": a draft is worked on, everything else is
            read. Saying "Edit" over a published contest would offer something that is refused. */}
        {status === 'draft' ? 'Continue setting up' : 'Open'}
      </button>
    </article>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">{label}</dt>
      <dd className="mt-0.5 truncate">{children}</dd>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-line bg-surface p-12 text-center">
      <Sparkles className="mx-auto mb-3 size-9 text-ink-800/20" />
      <h2 className="font-display font-semibold text-ink-950">No contests yet</h2>
      <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-800/60">
        A contest gives your regulars a reason to follow a series they are not playing in. They
        pick eight of your players under a budget before it starts, and score whatever those
        players score.
      </p>
    </div>
  );
}

/**
 * §6's order, then by name within a stage.
 *
 * <b>Name rather than "newest first", because there is no date to sort on.</b> `FantasyContestDto`
 * carries `publishedAt`, `lockedAt` and `closedAt` and no `createdAt`, so every draft — the stage
 * this ordering matters most in — would have nothing to compare. Ordering on the key instead would
 * work today and for a reason nobody should rely on: the ids are time-ordered v7 GUIDs, which is a
 * property of how they are minted rather than anything the contract states.
 *
 * Sorted on a copy: the query's array is cached and TanStack hands back the same reference, so
 * sorting in place mutates what every other reader of that cache sees.
 */
function byLifecycle(contests: readonly FantasyContest[]): FantasyContest[] {
  return [...contests].sort(
    (a, b) =>
      CONTEST_STATUSES.indexOf(contestStatusOf(a)) - CONTEST_STATUSES.indexOf(contestStatusOf(b)) ||
      a.name.localeCompare(b.name) ||
      a.id.localeCompare(b.id),
  );
}
